import { useEffect, useCallback, useRef, useState, lazy, Suspense } from 'react';
import { useSettingsStore } from './stores/settingsStore';
import { useProviderStore } from './stores/providerStore';
import { useCoreStore } from './stores/coreStore';
import { useSelectionStore } from './stores/selectionStore';
import { useUIStore } from './stores/uiStore';
import { useCanvasStore } from './stores/canvasStore';
import { scrollContainerRef, svgRef, chatEndRef } from './stores/refs';
import { useLLMProvider } from './hooks/useLLMProvider';
import { createProvider, SystemProvider } from './providers';
import { useCanvasInteraction } from './hooks/useCanvasInteraction';
import { useNodeOperations } from './hooks/useNodeOperations';
import { usePersistence } from './hooks/usePersistence';
import { useInfoGathering } from './hooks/useInfoGathering';
import { useFlashcardHandlers } from './hooks/useFlashcardHandlers';
import { useMapSync } from './hooks/useMapSync';
import { usePostItHandlers } from './hooks/usePostItHandlers';
import { useSendMessage } from './hooks/useSendMessage';
import { useAIActions } from './hooks/useAIActions';
import { copyToClipboard } from './utils/export';
import { normalizeNodeFlashcard } from './utils/storage';
import { useAuth } from './hooks/useAuth';
import { useIsMobile } from './hooks/useIsMobile';
import { centerCanvasInitial } from './utils/canvas';
import { ZOOM_MIN, ZOOM_MAX, ZOOM_FACTOR } from './constants';
import { getSettings, setSettings, clearSetting } from './utils/storage';
import { useLatestRef } from './hooks/useLatestRef';

import { PanelLeft, Crosshair, MessageSquare } from 'lucide-react';

import { useCanvasActions } from './hooks/app/useCanvasActions';
import { useNodeInteraction } from './hooks/app/useNodeInteraction';
import { useMapOperations } from './hooks/app/useMapOperations';
import { useTouchZoom } from './hooks/app/useTouchZoom';
import { useImportMap } from './hooks/useImportMap';
import { useQuizSession } from './hooks/useQuizSession';

// Components — always rendered
import { Sidebar } from './components/Sidebar';
import { Canvas } from './components/Canvas';
import { ImportMapModal } from './components/ImportMapModal';

import { QuizModal } from './components/QuizModal';

// Components — lazy loaded
const InviteGate        = lazy(() => import('./components/InviteGate').then(m => ({ default: m.InviteGate })));
const LoginScreen       = lazy(() => import('./components/LoginScreen').then(m => ({ default: m.LoginScreen })));
const ProviderSetup     = lazy(() => import('./components/ProviderSetup').then(m => ({ default: m.ProviderSetup })));
const NodeDetailsModal  = lazy(() => import('./components/NodeDetailsModal').then(m => ({ default: m.NodeDetailsModal })));
const EditNodeModal     = lazy(() => import('./components/EditNodeModal').then(m => ({ default: m.EditNodeModal })));
const MermaidModal      = lazy(() => import('./components/MermaidModal').then(m => ({ default: m.MermaidModal })));
const ColorPickerModal  = lazy(() => import('./components/ColorPickerModal').then(m => ({ default: m.ColorPickerModal })));
const ConfirmModal      = lazy(() => import('./components/ConfirmModal').then(m => ({ default: m.ConfirmModal })));
const ReportModal       = lazy(() => import('./components/ReportModal').then(m => ({ default: m.ReportModal })));
const ShareModal        = lazy(() => import('./components/ShareModal').then(m => ({ default: m.ShareModal })));
const MergeModal        = lazy(() => import('./components/MergeModal').then(m => ({ default: m.MergeModal })));
const OnboardingSlideshow = lazy(() => import('./components/OnboardingSlideshow').then(m => ({ default: m.OnboardingSlideshow })));
const TemplatePickerModal = lazy(() => import('./components/TemplatePickerModal').then(m => ({ default: m.TemplatePickerModal })));

const FullPageSpinner = () => (
  <div className="flex items-center justify-center min-h-screen" style={{ background: 'var(--bg)' }}>
    <span className="w-6 h-6 rounded-full border-2 animate-spin" style={{ borderColor: 'var(--border)', borderTopColor: 'var(--accent)' }} />
  </div>
);

function AppContent() {
  // --- Zustand stores ---
  const { nodes, setNodes, connections, setConnections, messages, setMessages, postIts, setPostIts, reports, setReports, input, setInput, exchangesWithoutNodes, setExchangesWithoutNodes } = useCoreStore();
  const { selectedNode, setSelectedNode, selectedNodes, setSelectedNodes, hoveredNode, setHoveredNode, selectedConnection, setSelectedConnection, revealedFlashcards, setRevealedFlashcards } = useSelectionStore();
  const { modalNode, setModalNode, editNode, setEditNode, editTitle, setEditTitle, editSummary, setEditSummary, editDescription, setEditDescription, colorPickerNode, setColorPickerNode, selectedColor, setSelectedColor, showMermaidModal, setShowMermaidModal, mermaidCode, setMermaidCode, copiedToClipboard, setCopiedToClipboard, sidebarVisible, setSidebarVisible } = useUIStore();
  const { providerConfig, setProviderConfig, isLoading, setIsLoading, loadingStatus, setLoadingStatus, suggestions, setSuggestions } = useProviderStore();
  const { currentMode, setCurrentMode, mapContext, setMapContext, userContext, setUserContext, lang, flashcardMode, setFlashcardMode, showFlashcardIcons, setShowFlashcardIcons } = useSettingsStore();
  const { zoom, setZoom, isPanMode, setIsPanMode } = useCanvasStore();

  const { user, authLoading, isAllowed, signInWithGoogle, signOut, redeemCode } = useAuth();
  const [showOnboarding, setShowOnboarding] = useState(() => !getSettings().onboardingDone);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [showImportBook, setShowImportBook] = useState(false);
  const [showQuiz, setShowQuiz] = useState(false);
  const isMobile = useIsMobile();

  const newNodePositionRef = useRef(null);
  const selectedNodeForNewRef = useRef(null);
  const mouseMoveRafRef = useRef(null);
  const zoomRef = useLatestRef(zoom);
  const nodesRef = useLatestRef(nodes);
  const supportsPointer = typeof window !== 'undefined' && 'PointerEvent' in window;

  const { createNode, updateNode, deleteNode, toggleBan, createConnection, deleteConnection, createMultipleNodes, resetAll } = useNodeOperations(setNodes, setConnections, scrollContainerRef);
  const provider = providerConfig ? createProvider(providerConfig.provider, providerConfig.apiKey, { model: providerConfig.model }) : (user ? new SystemProvider() : null);

  const {
    currentMapId, currentMapName, maps,
    switchMap, createNewMap, deleteMapById, renameCurrentMap,
    exportCurrentMap, importMap, saveReport, deleteReport,
    reloadCurrentMap, refreshMaps
  } = usePersistence(
    { nodes, connections, messages, postIts, reports, currentMode, mapContext },
    { setNodes, setConnections, setMessages, setPostIts, setReports, setCurrentMode, setMapContext },
    user
  );

  const { importFromImage, isLoading: isImporting, error: importError, clearError: clearImportError, captureStatus, captureUrl, startCapture, stopCapture } = useImportMap(provider, createNewMap);

  const quizSession = useQuizSession({ nodes, connections, mapId: currentMapId, provider, lang });

  const api = useLLMProvider(provider, lang);
  const { sendConversationalMessage, suggestSolutions, enhanceSolution, analyzeMap, generateFlashcard, generateNodeContent, improveContext } = api;
  const infoGathering = useInfoGathering(api);

  const { selectionState, handleMouseDown, handleMouseMove, handleMouseUp, handleCanvasMouseDown } = useCanvasInteraction(scrollContainerRef, zoom);

  const {
    handleToggleFlashcardMode, handleToggleFlashcardIcons, handleToggleFlashcard,
    handleRevealFlashcard, handleSetFlashcardStatus, handleGenerateFlashcard, handleGenerateContent,
    flashcardStats
  } = useFlashcardHandlers({
    nodes, connections, updateNode, generateFlashcard, generateNodeContent,
    flashcardMode, showFlashcardIcons,
    setFlashcardMode, setShowFlashcardIcons, setRevealedFlashcards, setSelectedNode
  });

  const { handleSendMessage } = useSendMessage({
    input, isLoading, infoGathering, nodes, connections, messages,
    sendConversationalMessage, currentMapName, renameCurrentMap,
    exchangesWithoutNodes, setExchangesWithoutNodes,
    setMessages, setInput, setSuggestions, setIsLoading, setNodes, setConnections,
    currentMode, userContext, mapContext, setLoadingStatus, lang
  });

  const { isAnalyzing, reportData, setReportData, showReportModal, setShowReportModal, shareModal, setShareModal, handleSuggestSolutions, handleEnhance, handleAnalyzeMap, handleShareMap, handleCreateActions } = useAIActions({
    nodes, connections, postIts, selectedNode, isLoading,
    setIsLoading, setMessages, setModalNode, suggestSolutions, enhanceSolution, analyzeMap, saveReport, createMultipleNodes, createNode, currentMapName
  });

  // Map sync
  const { mergePlan, showMergeModal, setShowMergeModal, setMergePlan, handleMergeConfirm } = useMapSync({ user, currentMapId, reloadCurrentMap, refreshMaps, switchMap });
  const { handleAddPostIt, handleDeletePostIt, handlePostItTextChange, handlePostItColorChange, handlePostItDragStart, handlePostItResizeStart, dragPostItRef, resizePostItRef } = usePostItHandlers({ postIts, setPostIts, scrollContainerRef, zoomRef });

  const { handleZoomIn, handleZoomOut, handleResetZoom, handleCenterCanvas, handleExportPNG, handleExportJSON, handleExportMermaid } = useCanvasActions({
    nodes, connections, postIts, currentMapName, scrollContainerRef, zoom, setZoom, zoomRef, setMermaidCode, setShowMermaidModal, setCopiedToClipboard
  });

  const { handleNodeClick: handleNodeClickRaw, handleFocusNode, handleMarkNode, handleEditNode, handleColorNode, handleAddNode: handleAddNodeRaw, handleBulkDelete, handleDeleteNode, handleStartInfoGathering } = useNodeInteraction({
    nodes, setNodes, connections, setConnections, selectedNode, setSelectedNode, selectedNodes, setSelectedNodes, modalNode, setModalNode,
    editNode, setEditNode, setEditTitle, setEditSummary, setEditDescription, setColorPickerNode, setSelectedColor, scrollContainerRef, zoom,
    updateNode, deleteNode, toggleBan, setMessages, setConfirmDialog, infoGathering
  });

  const handleNodeClick = useCallback((e, node) => {
    handleNodeClickRaw(e, node);
  }, [handleNodeClickRaw]);

  const { handleSelectTemplate: handleSelectTemplateRaw, handleReset } = useMapOperations({
    createNewMap, setCurrentMode, setNodes, setConnections, setMessages, setSuggestions, setExchangesWithoutNodes, setPostIts, setSelectedNode, setSelectedNodes, setSelectedConnection, setModalNode, setInput,
    resetAll, scrollContainerRef, zoom, setMapContext
  });

  const handleSelectTemplate = useCallback((template) => {
    handleSelectTemplateRaw(template);
    setShowTemplatePicker(false);
    if (isMobile) {
      setSidebarVisible(true);
    }
  }, [handleSelectTemplateRaw, isMobile, setShowTemplatePicker, setSidebarVisible]);

  useTouchZoom({ scrollContainerRef, zoomRef, setZoom, setNodes, supportsPointer });

  const handleAddNode = useCallback((p) => handleAddNodeRaw(p, newNodePositionRef, selectedNodeForNewRef), [handleAddNodeRaw]);

  const handleImproveUserContext = useCallback(async () => {
    if (!userContext.trim()) return;
    const improved = await improveContext(userContext, 'user');
    setUserContext(improved);
  }, [userContext, improveContext, setUserContext]);

  const handleImproveMapContext = useCallback(async () => {
    if (!mapContext.trim()) return;
    const improved = await improveContext(mapContext, 'map');
    setMapContext(improved);
  }, [mapContext, improveContext, setMapContext]);

  const handleCopyToPostIt = useCallback((content) => {
    const container = scrollContainerRef.current;
    const cx = container ? (container.scrollLeft + container.clientWidth / 2) / zoom : 4000;
    const cy = container ? (container.scrollTop + container.clientHeight / 2) / zoom : 4000;
    setPostIts(prev => [...prev, {
      id: `p-${Date.now()}`,
      x: cx - 100,
      y: cy - 70,
      text: content,
      color: 'yellow'
    }]);
  }, [zoom, setPostIts]);

  useEffect(() => {
    const isEditable = () => {
      const el = document.activeElement;
      return el.tagName === 'TEXTAREA' || el.tagName === 'INPUT' || el.isContentEditable;
    };
    const handleKeyDown = (e) => { if (e.code === 'Space' && !isEditable()) { e.preventDefault(); if (!e.repeat) setIsPanMode(true); } };
    const handleKeyUp = (e) => { if (e.code === 'Space' && !isEditable()) { e.preventDefault(); setIsPanMode(false); } };
    window.addEventListener('keydown', handleKeyDown, { capture: true });
    window.addEventListener('keyup', handleKeyUp, { capture: true });
    return () => { window.removeEventListener('keydown', handleKeyDown, { capture: true }); window.removeEventListener('keyup', handleKeyUp, { capture: true }); };
  }, [setIsPanMode]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === '0' && document.activeElement.tagName !== 'TEXTAREA' && document.activeElement.tagName !== 'INPUT') {
        e.preventDefault();
        handleResetZoom();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleResetZoom]);

  // Center when map changes. Polls until Canvas is actually mounted.
  useEffect(() => {
    if (!currentMapId) return;
    setZoom(1);

    let attempts = 0;
    let timerId;

    const tryCenter = () => {
      const container = scrollContainerRef.current;
      if (!container) {
        if (attempts++ < 30) timerId = setTimeout(tryCenter, 50);
        return;
      }
      const currentNodes = nodesRef.current;
      if (currentNodes.length === 0) {
        centerCanvasInitial(container, 1);
      } else {
        const cx = currentNodes.reduce((s, n) => s + n.x, 0) / currentNodes.length;
        const cy = currentNodes.reduce((s, n) => s + n.y, 0) / currentNodes.length;
        container.scrollLeft = Math.max(0, cx - container.clientWidth / 2);
        container.scrollTop = Math.max(0, cy - container.clientHeight / 2);
      }
    };

    timerId = setTimeout(tryCenter, 50);
    return () => clearTimeout(timerId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMapId]);

  const handleSelectConnection = useCallback((conn) => {
    setSelectedConnection(prev => prev?.id === conn.id ? null : conn);
    setSelectedNode(null);
    setSelectedNodes([]);
  }, [setSelectedConnection, setSelectedNode, setSelectedNodes]);

  const handleFocusNodeById = useCallback((nodeId, opts = {}) => {
    const node = nodesRef.current.find(n => n.id === nodeId);
    if (!node) return;
    if (!opts.keepOpen) setShowReportModal(false);
    setSelectedNodes([node.id]);
    setSelectedNode(null);
    setModalNode(null);
    const container = scrollContainerRef.current;
    if (!container) return;
    const left = node.x * zoom - container.clientWidth / 2;
    const top = node.y * zoom - container.clientHeight / 2;
    container.scrollTo({ left, top, behavior: 'smooth' });
  }, [zoom, setSelectedNodes, setSelectedNode, setModalNode, setShowReportModal, nodesRef]);

  const handleMouseMoveWrapper = useCallback((e) => {
    const { clientX, clientY } = e;
    if (mouseMoveRafRef.current) cancelAnimationFrame(mouseMoveRafRef.current);
    mouseMoveRafRef.current = requestAnimationFrame(() => {
      handleMouseMove({ clientX, clientY }, nodesRef.current, setNodes);
      if (dragPostItRef.current) {
        const container = scrollContainerRef.current;
        if (!container) return;
        const rect = container.getBoundingClientRect();
        const { id, offsetX, offsetY } = dragPostItRef.current;
        const canvasX = (clientX - rect.left + container.scrollLeft) / zoomRef.current;
        const canvasY = (clientY - rect.top + container.scrollTop) / zoomRef.current;
        setPostIts(prev => prev.map(p => p.id === id ? { ...p, x: canvasX - offsetX, y: canvasY - offsetY } : p));
      }
      if (resizePostItRef.current) {
        const { id, startClientX, startClientY, startW, startH } = resizePostItRef.current;
        const dw = (clientX - startClientX) / zoomRef.current;
        const dh = (clientY - startClientY) / zoomRef.current;
        setPostIts(prev => prev.map(p => p.id === id ? { ...p, w: Math.max(140, startW + dw), h: Math.max(80, startH + dh) } : p));
      }
      mouseMoveRafRef.current = null;
    });
  }, [handleMouseMove, setNodes, setPostIts, zoomRef, nodesRef, dragPostItRef, resizePostItRef]);

  const handlePointerMoveWrapper = useCallback((e) => {
    if (e.pointerType && !['mouse', 'pen', 'touch'].includes(e.pointerType)) return;
    handleMouseMoveWrapper(e);
  }, [handleMouseMoveWrapper]);

  const handlePointerUpWrapper = useCallback((e) => {
    if (e.pointerType && !['mouse', 'pen', 'touch'].includes(e.pointerType)) return;
    handleMouseUp(nodes, setSelectedNodes);
    dragPostItRef.current = null;
    resizePostItRef.current = null;
  }, [handleMouseUp, nodes, setSelectedNodes, dragPostItRef, resizePostItRef]);

  if (authLoading || (user && isAllowed === null)) return <FullPageSpinner />;
  if (showOnboarding) return <Suspense fallback={<FullPageSpinner />}><OnboardingSlideshow onComplete={() => { setSettings({ onboardingDone: true }); setShowOnboarding(false); }} /></Suspense>;
  if (!user) return <Suspense fallback={<FullPageSpinner />}><LoginScreen onSignIn={signInWithGoogle} /></Suspense>;
  if (!isAllowed) return <Suspense fallback={<FullPageSpinner />}><InviteGate user={user} onRedeem={redeemCode} onSignOut={signOut} /></Suspense>;

  return (
    <div
      className="flex h-screen overflow-hidden"
      style={{ background: 'var(--bg)' }}
      onMouseMove={!supportsPointer ? handleMouseMoveWrapper : undefined}
      onMouseUp={!supportsPointer ? () => { handleMouseUp(nodes, setSelectedNodes); dragPostItRef.current = null; resizePostItRef.current = null; } : undefined}
      onPointerMove={supportsPointer ? handlePointerMoveWrapper : undefined}
      onPointerUp={supportsPointer ? handlePointerUpWrapper : undefined}
      onPointerCancel={supportsPointer ? handlePointerUpWrapper : undefined}
      onTouchMove={(e) => {
        if (supportsPointer) return;
        if (e.touches.length === 1) {
          handleMouseMoveWrapper({ clientX: e.touches[0].clientX, clientY: e.touches[0].clientY });
        }
      }}
      onTouchEnd={() => { 
        if (supportsPointer) return;
        handleMouseUp(nodes, setSelectedNodes); 
        dragPostItRef.current = null; 
        resizePostItRef.current = null; 
      }}
    >
      {!sidebarVisible && !isMobile && (
        <button
          onClick={() => setSidebarVisible(true)}
          className="fixed top-5 left-5 z-50 w-11 h-11 flex items-center justify-center rounded-[18px] transition-all duration-300 hover:scale-105"
          style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-muted)' }}
        >
          <PanelLeft size={20} />
        </button>
      )}
      {isMobile && !sidebarVisible && (
        <div className="fixed top-4 right-4 z-50 flex gap-2">
          <button
            onClick={handleCenterCanvas}
            className="w-11 h-11 flex items-center justify-center rounded-[18px] active:scale-95"
            style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-muted)' }}
          >
            <Crosshair size={20} />
          </button>
          <button
            onClick={() => setSidebarVisible(true)}
            className="w-11 h-11 flex items-center justify-center rounded-[18px] active:scale-95"
            style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-muted)' }}
          >
            <MessageSquare size={20} />
          </button>
        </div>
      )}
      <div className={`h-full transition-all duration-300 ease-in-out ${
        isMobile
          ? `fixed inset-0 z-50 ${sidebarVisible ? 'translate-x-0' : '-translate-x-full'}`
          : `${sidebarVisible ? 'w-[400px] min-w-[400px]' : 'w-0 min-w-0'} overflow-hidden`
      }`}
        style={{ background: isMobile ? 'var(--bg-dim)' : undefined }}
      >
        <Sidebar
          messages={messages} isLoading={isLoading} input={input} onInputChange={setInput} onSendMessage={handleSendMessage} onReset={handleReset} 
          onClearChat={() => { setMessages([]); setSuggestions([]); }}
          onChangeProvider={() => {
            setConfirmDialog({
              message: 'Cambiare provider?',
              danger: false,
              confirmLabel: 'Cambia',
              onConfirm: () => {
                setProviderConfig(null);
                clearSetting('providerConfig');
              }
            });
          }}
          providerName={providerConfig?.providerName || (user ? 'LEARNATLAS AI' : 'AI')} onToggleSidebar={() => setSidebarVisible(false)}
          chatEndRef={chatEndRef} maps={maps} currentMapId={currentMapId} currentMapName={currentMapName} onSwitchMap={(id) => { switchMap(id); setSuggestions([]); setExchangesWithoutNodes(0); }}
          onCreateMap={() => setShowTemplatePicker(true)} onDeleteMap={(id) => { deleteMapById(id); setSuggestions([]); setExchangesWithoutNodes(0); }} onRenameMap={renameCurrentMap} onExportMap={exportCurrentMap} onImportMap={importMap}
          onImportFromBook={() => { clearImportError(); setShowImportBook(true); }}
          onStartQuiz={() => setShowQuiz(true)}
          isInfoGatheringActive={infoGathering.isInfoGatheringActive} onStopInfoGathering={infoGathering.stopInfoGathering} suggestions={suggestions} onSuggestionClick={handleSendMessage}
          onCopyToPostIt={handleCopyToPostIt}
          user={user} onSignIn={signInWithGoogle} onSignOut={signOut} currentMode={currentMode} onModeChange={setCurrentMode} userContext={userContext} onUserContextChange={setUserContext}
          mapContext={mapContext} onMapContextChange={setMapContext} onImproveUserContext={handleImproveUserContext} onImproveMapContext={handleImproveMapContext}
          loadingStatus={loadingStatus}
          onShowTutorial={() => setShowOnboarding(true)}
        />
      </div>
      <Canvas
        nodes={nodes} connections={connections} scrollContainerRef={scrollContainerRef} svgRef={svgRef} zoom={zoom} isMobile={isMobile}
        selectionState={selectionState} selectedNodes={selectedNodes} selectedNode={selectedNode} hoveredNode={hoveredNode} onNodeMouseDown={(e, id) => handleMouseDown(e, id, nodes, selectedNodes, setSelectedNodes)}
        onNodeClick={handleNodeClick} onNodeHover={setHoveredNode} onCanvasMouseDown={(e) => { 
          handleCanvasMouseDown(e, setSelectedNode, setModalNode, setSelectedNodes, isPanMode); 
          setSelectedConnection(null);
        }}
        onToggleBan={toggleBan} onFocusNode={handleFocusNode} onMarkNode={handleMarkNode} onEditNode={handleEditNode} onColorNode={handleColorNode} onSuggestSolutions={handleSuggestSolutions} onDeleteNode={handleDeleteNode}
        onCenterCanvas={handleCenterCanvas} onExportPNG={handleExportPNG} onExportMermaid={handleExportMermaid} onExportJSON={handleExportJSON} isPanMode={isPanMode} onTogglePanMode={() => setIsPanMode(!isPanMode)}
        onBulkToggleBan={() => { selectedNodes.forEach(id => toggleBan(id)); }} onBulkDelete={handleBulkDelete} onBulkColorChange={() => { const first = nodes.find(n => n.id === selectedNodes[0]); setColorPickerNode({ id: 'bulk', isBulk: true }); setSelectedColor(first?.color || '#22c55e'); }}
        onAddNode={handleAddNode} activeParentNode={modalNode} onAnalyzeMap={handleAnalyzeMap} onShareMap={handleShareMap} hasNodes={nodes.length > 0} isAnalyzing={isAnalyzing} reports={reports}
        onOpenReport={(entry) => { setReportData(entry.data); setShowReportModal(true); }} onResetZoom={handleResetZoom} onZoomIn={handleZoomIn} onZoomOut={handleZoomOut}
        flashcardMode={flashcardMode} showFlashcardIcons={showFlashcardIcons} revealedFlashcards={revealedFlashcards} onToggleFlashcardMode={handleToggleFlashcardMode} onToggleFlashcardIcons={handleToggleFlashcardIcons}
        onToggleFlashcard={handleToggleFlashcard} onRevealFlashcard={handleRevealFlashcard} onSetFlashcardStatus={handleSetFlashcardStatus} flashcardStats={flashcardStats} onStartInfoGathering={handleStartInfoGathering}
        selectedConnection={selectedConnection} onSelectConnection={handleSelectConnection} onDeleteConnection={(id) => { deleteConnection(id); setSelectedConnection(null); }}
        onColorChangeConnection={(id, color) => { setConnections(prev => prev.map(c => c.id === id ? { ...c, color } : c)); setSelectedConnection(null); }}
        onStrokeChangeConnection={(id, ch) => { setConnections(prev => prev.map(c => c.id === id ? { ...c, ...ch } : c)); setSelectedConnection(prev => prev?.id === id ? { ...prev, ...ch } : prev); }}
        onLabelChangeConnection={(id, label) => { setConnections(prev => prev.map(c => c.id === id ? { ...c, label } : c)); setSelectedConnection(prev => prev?.id === id ? { ...prev, label } : prev); }}
        onCreateConnection={(f, t) => { if (!connections.some(c => c.from === f && c.to === t)) createConnection(f, t, nodes); }}
        postIts={postIts} onAddPostIt={handleAddPostIt} onDeletePostIt={handleDeletePostIt} onPostItTextChange={handlePostItTextChange} onPostItColorChange={handlePostItColorChange} onPostItDragStart={handlePostItDragStart} onPostItResizeStart={handlePostItResizeStart} onPostItExitPanMode={() => setIsPanMode(false)}
        isAILoading={isLoading}
      />
      <Suspense fallback={null}>
        <NodeDetailsModal node={modalNode} isOpen={!!modalNode} onClose={() => setModalNode(null)} onSuggestSolutions={handleSuggestSolutions} onEnhanceSteps={(n) => handleEnhance(n, 'steps')} onEnhanceRisks={(n) => handleEnhance(n, 'risks')} isLoading={isLoading} onStartInfoGathering={handleStartInfoGathering} />
        <EditNodeModal node={editNode} isOpen={!!editNode} onClose={() => setEditNode(null)} onSave={() => {
          const title = editTitle.trim(); const summary = (editSummary || '').trim(); const description = (editDescription || '').trim(); if (!title) return;
          if (editNode.id === '__new__') {
            const pos = newNodePositionRef.current || { x: 4000, y: 4000 }; const newId = Date.now().toString(); const newNode = normalizeNodeFlashcard({ id: newId, type: editNode.type, title, summary, description, x: pos.x, y: pos.y, banned: false, color: (editNode.type === 'problem' ? '#f87171' : '#22c55e') });
            setNodes(prev => [...prev, newNode]); const parent = selectedNodeForNewRef.current; if (parent) setConnections(prev => [...prev, { id: `${parent.id}-${newId}`, from: parent.id, to: newId, _animateIn: true }]);
            setEditNode(null); return;
          }
          updateNode(editNode.id, { title, summary, description }); setEditNode(null);
        }} editTitle={editTitle} editSummary={editSummary} editDescription={editDescription} onTitleChange={setEditTitle} onSummaryChange={setEditSummary} onDescriptionChange={setEditDescription} onGenerateFlashcard={handleGenerateFlashcard} onGenerateContent={handleGenerateContent} />
        <MermaidModal isOpen={showMermaidModal} onClose={() => setShowMermaidModal(false)} mermaidCode={mermaidCode} copied={copiedToClipboard} onCopy={async () => { await copyToClipboard(mermaidCode); setCopiedToClipboard(true); setTimeout(() => setCopiedToClipboard(false), 2000); }} />
        <ColorPickerModal node={colorPickerNode} isOpen={!!colorPickerNode} onClose={() => setColorPickerNode(null)} selectedColor={selectedColor} onColorChange={setSelectedColor} onApply={() => { if (selectedNodes.length > 0) { selectedNodes.forEach(id => updateNode(id, { color: selectedColor })); setSelectedNodes([]); } else if (colorPickerNode) updateNode(colorPickerNode.id, { color: selectedColor }); setColorPickerNode(null); }} />
        <ReportModal
          isOpen={showReportModal}
          onClose={() => setShowReportModal(false)}
          report={reportData}
          reports={reports}
          mapName={currentMapName}
          onDeleteReport={deleteReport}
          onOpenReport={(entry) => setReportData({ ...entry.data, _reportId: entry.id, _createdAt: entry.createdAt })}
          onFocusNode={handleFocusNodeById}
          onCreateActions={handleCreateActions}
          onCopyToPostIt={handleCopyToPostIt}
        />
        <ConfirmModal isOpen={!!confirmDialog} message={confirmDialog?.message} danger={confirmDialog?.danger} confirmLabel={confirmDialog?.confirmLabel} onConfirm={() => { confirmDialog?.onConfirm?.(); setConfirmDialog(null); }} onCancel={() => setConfirmDialog(null)} />
        <ShareModal isOpen={shareModal.open} onClose={() => setShareModal({ open: false, url: null, isSharing: false })} shareUrl={shareModal.url} isSharing={shareModal.isSharing} />
        <MergeModal isOpen={showMergeModal} plan={mergePlan} onConfirm={handleMergeConfirm} onSkip={() => { setShowMergeModal(false); setMergePlan(null); }} />
        <TemplatePickerModal isOpen={showTemplatePicker} onSelect={handleSelectTemplate} onCancel={() => setShowTemplatePicker(false)} />
      </Suspense>
      <QuizModal
        isOpen={showQuiz}
        onClose={() => setShowQuiz(false)}
        nodes={nodes}
        useQuizSession={quizSession}
      />
      {showImportBook && (
        <ImportMapModal
          onImport={importFromImage}
          isLoading={isImporting}
          error={importError}
          onClose={() => { setShowImportBook(false); stopCapture(); }}
          captureStatus={captureStatus}
          captureUrl={captureUrl}
          onStartCapture={startCapture}
          onStopCapture={stopCapture}
        />
      )}
    </div>
  );
}

function App() { return <AppContent />; }
export default App;
